/**
 * API / 通信の例外を、利用者向けの日本語メッセージに変換する共通ユーティリティ (Issue #815)。
 *
 * ## なぜ必要か
 *
 * これまで画面ごとに `[HTTP 403] Forbidden` のような「HTTP ステータス + サーバの英語メッセージ」を
 * そのまま表示していた。現場の運用者には何が起きたのか、何をすればよいのかが伝わらない。
 * ここでは HTTP ステータスごとに「状況 + 次の行動」を日本語で返す。
 *
 * ## 方針
 *
 * - 401 / 403 / 404 / 429: サーバの英語メッセージは使わず、ステータスごとの日本語に置き換える。
 * - 5xx: 英語 (API Gateway / Lambda 既定の `Internal server error` など) は日本語に置き換える。
 *   サーバが日本語の文言を返したときはそのまま使う。500 (内部障害) と 502 (運行情報の取得先に
 *   届かない) のように、利用者が次の行動を変えられる区別を画面に残すため。
 * - 400 / 409 / その他 4xx: サーバのメッセージが日本語ならそのまま使う (すでに利用者向け)。
 *   英語なら原文を「詳細」として添える (調査用に残す)。
 * - `TypeError` (`fetch` が通信失敗時に投げる): ネットワーク確認の案内。
 * - その他の `Error`: 日本語ならそのまま。英語なら「予期しないエラー」+ 詳細。
 *
 * 画面専用の対応表を持つ画面 (SSML 辞書 / 任意テキスト / 権限管理) は、それぞれの
 * `*Messages.ts` で原因の入力欄まで特定している。このモジュールはそれ以外の画面の共通の受け皿。
 */
import { ApiError } from '../api/common';

/** 通信そのものに失敗したとき (fetch が TypeError を投げる) */
export const NETWORK_ERROR_MESSAGE = '通信に失敗しました。ネットワークの接続を確認してください';

/** 原因が分からない例外 */
export const UNEXPECTED_ERROR_MESSAGE = '予期しないエラーが発生しました';

/** 401: 認証切れ */
export const UNAUTHORIZED_MESSAGE = 'ログインの有効期限が切れました。再度ログインしてください';

/** 403: 権限なし */
export const FORBIDDEN_MESSAGE =
  'この操作を行う権限がありません。権限が変更された可能性があるため、ページを再読み込みしてください。解決しない場合は管理者に確認してください';

/** 404: 対象なし */
export const NOT_FOUND_MESSAGE =
  '対象が見つかりません。すでに削除された可能性があります。一覧を再読み込みしてください';

/** 429: リクエスト過多 */
export const TOO_MANY_REQUESTS_MESSAGE =
  'リクエストが集中しています。しばらく待ってから、もう一度お試しください';

/** 5xx: サーバエラー */
export const SERVER_ERROR_MESSAGE =
  'サーバでエラーが発生しました。時間をおいて、もう一度お試しください';

/** 日本語 (ひらがな / カタカナ / 漢字) を含むか。含むなら既に利用者向けの文とみなす。 */
export function containsJapanese(text: string): boolean {
  return /[\u3040-\u30ff\u3400-\u9fff]/.test(text);
}

/** HTTP ステータスとサーバのメッセージから、日本語のメッセージを作る。 */
export function describeHttpStatus(status: number, serverMessage: string): string {
  if (status === 401) return UNAUTHORIZED_MESSAGE;
  if (status === 403) return FORBIDDEN_MESSAGE;
  if (status === 404) return NOT_FOUND_MESSAGE;
  if (status === 429) return TOO_MANY_REQUESTS_MESSAGE;
  const detail = serverMessage.trim();
  if (status >= 500) {
    return detail !== '' && containsJapanese(detail) ? detail : SERVER_ERROR_MESSAGE;
  }
  if (detail !== '' && containsJapanese(detail)) return detail;
  const suffix = detail !== '' ? `(詳細: ${detail})` : '';
  if (status === 400) return `入力内容を確認してください${suffix}`;
  if (status === 409) return `現在の状態ではこの操作を実行できません${suffix}`;
  return `処理に失敗しました(HTTP ${status})${suffix}`;
}

/**
 * API クライアントの例外を、画面表示用の日本語メッセージに変換する。
 *
 * @param err 捕捉した例外 (`unknown`)
 * @param fallback 例外が `Error` でないときに使うメッセージ。省略時は {@link UNEXPECTED_ERROR_MESSAGE}
 */
export function describeApiErrorMessage(err: unknown, fallback?: string): string {
  if (err instanceof ApiError) return describeHttpStatus(err.status, err.message);
  if (err instanceof TypeError) return NETWORK_ERROR_MESSAGE;
  if (err instanceof Error) {
    const message = err.message.trim();
    if (message === '') return fallback ?? UNEXPECTED_ERROR_MESSAGE;
    return containsJapanese(message) ? message : `${UNEXPECTED_ERROR_MESSAGE}(詳細: ${message})`;
  }
  return fallback ?? UNEXPECTED_ERROR_MESSAGE;
}
